import { Backdrop, Box, Fade, Typography } from '@mui/material';
import React from 'react';
import Logo from './Logo';

export type LoadingSize = 'small' | 'medium' | 'large';
export type LoadingVariant = 'spinner' | 'pulse' | 'bounce' | 'wave' | 'dots';
export type LoadingMode = 'inline' | 'fullscreen' | 'overlay';

interface LoadingProps {
  size?: LoadingSize;
  variant?: LoadingVariant;
  mode?: LoadingMode;
  message?: string;
  fullScreen?: boolean; // Deprecated: use mode instead
  color?: string;
}

const Loading: React.FC<LoadingProps> = ({
  size = 'medium',
  variant = 'spinner',
  mode = 'inline',
  message,
  fullScreen,
  color = '#43A047',
}) => {
  // Backward compatibility
  const actualMode = fullScreen ? 'fullscreen' : mode;

  const sizeMap = {
    small: 32,
    medium: 64,
    large: 96,
  };

  const logoSize = sizeMap[size];

  // Animation keyframes
  const animations = {
    spinner: {
      '@keyframes logoSpin': {
        '0%': {
          transform: 'rotate(0deg) scale(1)',
        },
        '50%': {
          transform: 'rotate(180deg) scale(1.1)',
        },
        '100%': {
          transform: 'rotate(360deg) scale(1)',
        },
      },
      animation: 'logoSpin 2s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite',
    },
    pulse: {
      '@keyframes logoPulse': {
        '0%, 100%': {
          transform: 'scale(1)',
          opacity: 1,
        },
        '50%': {
          transform: 'scale(1.2)',
          opacity: 0.8,
        },
      },
      animation: 'logoPulse 1.5s ease-in-out infinite',
    },
    bounce: {
      '@keyframes logoBounce': {
        '0%, 100%': {
          transform: 'translateY(0) scale(1)',
        },
        '25%': {
          transform: 'translateY(-15px) scale(1.05)',
        },
        '50%': {
          transform: 'translateY(0) scale(1.1)',
        },
        '75%': {
          transform: 'translateY(-8px) scale(1.05)',
        },
      },
      animation: 'logoBounce 1.2s ease-in-out infinite',
    },
    wave: {
      '@keyframes logoWave': {
        '0%, 100%': {
          transform: 'rotate(0deg) translateY(0)',
        },
        '25%': {
          transform: 'rotate(-10deg) translateY(-5px)',
        },
        '50%': {
          transform: 'rotate(0deg) translateY(-10px)',
        },
        '75%': {
          transform: 'rotate(10deg) translateY(-5px)',
        },
      },
      animation: 'logoWave 1.5s ease-in-out infinite',
    },
    dots: {
      '@keyframes logoDots': {
        '0%, 100%': {
          transform: 'scale(1)',
          opacity: 1,
        },
        '33%': {
          transform: 'scale(0.8)',
          opacity: 0.6,
        },
        '66%': {
          transform: 'scale(1.2)',
          opacity: 0.8,
        },
      },
      animation: 'logoDots 1.8s ease-in-out infinite',
    },
  };

  const dotAnimations = {
    '@keyframes dot1': {
      '0%, 80%, 100%': { transform: 'scale(0)' },
      '40%': { transform: 'scale(1)' },
    },
    '@keyframes dot2': {
      '0%, 80%, 100%': { transform: 'scale(0)' },
      '20%': { transform: 'scale(1)' },
      '40%': { transform: 'scale(0)' },
    },
    '@keyframes dot3': {
      '0%, 80%, 100%': { transform: 'scale(0)' },
      '40%': { transform: 'scale(1)' },
    },
  };

  const renderLoadingContent = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      {/* Logo with animation */}
      <Box
        sx={{
          position: 'relative',
          ...animations[variant],
        }}
      >
        <Logo size={logoSize} />

        {/* Additional effects for certain variants */}
        {variant === 'pulse' && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: logoSize * 1.5,
              height: logoSize * 1.5,
              borderRadius: '50%',
              border: `3px solid ${color}`,
              opacity: 0.3,
              '@keyframes ripple': {
                '0%': {
                  transform: 'translate(-50%, -50%) scale(0.8)',
                  opacity: 0.5,
                },
                '100%': {
                  transform: 'translate(-50%, -50%) scale(1.5)',
                  opacity: 0,
                },
              },
              animation: 'ripple 2s ease-out infinite',
            }}
          />
        )}

        {variant === 'dots' && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -logoSize * 0.3,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 0.5,
              ...dotAnimations,
            }}
          >
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: color,
                  animation: `dot${index + 1} 1.4s ease-in-out infinite`,
                  animationDelay: `${index * 0.2}s`,
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Loading message */}
      {message && (
        <Fade in timeout={500}>
          <Typography
            variant={size === 'small' ? 'body2' : 'body1'}
            sx={{
              color: actualMode === 'fullscreen' ? '#666' : 'text.secondary',
              fontWeight: 500,
              textAlign: 'center',
              maxWidth: 300,
            }}
          >
            {message}
          </Typography>
        </Fade>
      )}

      {/* Wave variant - additional wave effect */}
      {variant === 'wave' && (
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            mt: 1,
            '@keyframes waveBar': {
              '0%, 100%': {
                transform: 'scaleY(0.5)',
                opacity: 0.5,
              },
              '50%': {
                transform: 'scaleY(1)',
                opacity: 1,
              },
            },
          }}
        >
          {[0, 1, 2, 3, 4].map((index) => (
            <Box
              key={index}
              sx={{
                width: 4,
                height: 20,
                bgcolor: color,
                borderRadius: 2,
                animation: 'waveBar 1s ease-in-out infinite',
                animationDelay: `${index * 0.1}s`,
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );

  // Inline mode
  if (actualMode === 'inline') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
        }}
      >
        {renderLoadingContent()}
      </Box>
    );
  }

  // Fullscreen mode
  if (actualMode === 'fullscreen') {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
        }}
      >
        {renderLoadingContent()}
      </Box>
    );
  }

  // Overlay mode
  return (
    <Backdrop
      open
      sx={{
        color: '#fff',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <Box
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 3,
          p: 4,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        {renderLoadingContent()}
      </Box>
    </Backdrop>
  );
};

export default Loading;
